type FunctionColorPaletteKey = 'light' | 'normal' | 'dark';
type ColorPaletteKey = FunctionColorPaletteKey;
export type LabelType = 'dark' | 'light';

export interface ColorOption {
  textColor: '#FFFFFF' | '#000000';
  color: string;
}

export interface PaletteColorOption extends ColorOption {
  palette: ColorPaletteKey;
}

export interface ColorPalette {
  key: ColorPaletteKey;
  icon: 'shade-light' | 'shade-normal' | 'shade-dark';
  colors: ColorOption[];
  default: boolean;
}

// prettier-ignore
export const functionColorOptions: PaletteColorOption[] = [
  { color: '#C7CFDD', textColor: '#000000', palette: 'light' },
  { color: '#F6CA9F', textColor: '#000000', palette: 'light' },
  { color: '#F9E6CF', textColor: '#000000', palette: 'light' },
  { color: '#FFEB57', textColor: '#000000', palette: 'light' },
  { color: '#D3FC7E', textColor: '#000000', palette: 'light' },
  { color: '#94FDFF', textColor: '#000000', palette: 'light' },
  { color: '#FDD2ED', textColor: '#000000', palette: 'light' },
  { color: '#92A1B9', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#BF6F4A', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#E69C69', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#C64524', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#FF5000', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#ED7614', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#FFA214', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#FFC825', textColor: '#000000', palette: 'normal' },
  { color: '#99E65F', textColor: '#000000', palette: 'normal' },
  { color: '#5AC54F', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#33984B', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#0098DC', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#00CDF9', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#0CF1FF', textColor: '#000000', palette: 'normal' },
  { color: '#F389F5', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#DB3FFD', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#CA52C9', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#C85086', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#F68187', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#F5555D', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#EA323C', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#C42430', textColor: '#FFFFFF', palette: 'normal' },
  { color: '#657392', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#424C6E', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#8A4836', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#891E2B', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#5D2C28', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#1E6F50', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#134C4C', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#00396D', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#0069AA', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#0C0293', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#3003D9', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#7A09FA', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#93388F', textColor: '#FFFFFF', palette: 'dark' },
  { color: '#622461', textColor: '#FFFFFF', palette: 'dark' },
];

export const toColorOption = (option: PaletteColorOption): ColorOption => {
  const { color, textColor } = option;

  return { color, textColor };
};

// NOTE: these conversions are be obsolete once label is replaced with textColor on the api
export const colorToLabel = (color: ColorOption['textColor']): LabelType => {
  return color === '#FFFFFF' ? 'light' : 'dark';
};

export const labelToColor = (label: LabelType): ColorOption['textColor'] => {
  return label === 'light' ? '#FFFFFF' : '#000000';
};

// Normalizes user hex input to '#RRGGBB' (uppercase). Accepts '#RGB', 'RGB',
// '#RRGGBB' and 'RRGGBB'; returns null for anything else (incl. alpha channels).
export const normalizeHexColor = (value: string): string | null => {
  const match = /^#?([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.exec(value.trim());
  if (!match) {
    return null;
  }
  const hex =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : match[1];

  return `#${hex.toUpperCase()}`;
};

// Picks white or black text for a background color using the WCAG relative
// luminance contrast rule: white text wins below the luminance threshold
// where its contrast ratio against the background drops under black's.
export const textColorForBackground = (
  backgroundColor: string,
): ColorOption['textColor'] => {
  const hex = normalizeHexColor(backgroundColor);
  if (!hex) {
    return '#000000';
  }
  const [r, g, b] = [1, 3, 5].map((offset) => {
    const channel = parseInt(hex.slice(offset, offset + 2), 16) / 255;

    return channel <= 0.04045
      ? channel / 12.92
      : Math.pow((channel + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  return luminance > 0.179 ? '#000000' : '#FFFFFF';
};

export const colorPaletteMirroredProjects = [
  '#000000', // Black
  '#E6194B', // Red
  '#3CB44B', // Green
  '#4363D8', // Blue
  '#FFE119', // Yellow
  '#673AB7', // Deep Purple
  '#469990', // Teal
  '#FFD8B1', // Apricot
  '#000075', // Navy
  '#9A6324', // Brown
  '#bFEF45', // Lime
];
